/**
 * STYLING: Plain Tailwind ONLY!
 * Language selector for the translation tool — searchable dropdown + free-text fallback.
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';

interface Language {
  code: string;
  name: string;
  localName: string;
}

const LANGUAGES: Language[] = [
  { code: 'de', name: 'German', localName: 'Deutsch' },
  { code: 'en', name: 'English', localName: 'English' },
  { code: 'fr', name: 'French', localName: 'Français' },
  { code: 'it', name: 'Italian', localName: 'Italiano' },
  { code: 'es', name: 'Spanish', localName: 'Español' },
  { code: 'nl', name: 'Dutch', localName: 'Nederlands' },
  { code: 'pt', name: 'Portuguese', localName: 'Português' },
  { code: 'pl', name: 'Polish', localName: 'Polski' },
  { code: 'cs', name: 'Czech', localName: 'Čeština' },
  { code: 'sk', name: 'Slovak', localName: 'Slovenčina' },
  { code: 'hu', name: 'Hungarian', localName: 'Magyar' },
  { code: 'ro', name: 'Romanian', localName: 'Română' },
  { code: 'bg', name: 'Bulgarian', localName: 'Български' },
  { code: 'hr', name: 'Croatian', localName: 'Hrvatski' },
  { code: 'sl', name: 'Slovenian', localName: 'Slovenščina' },
  { code: 'el', name: 'Greek', localName: 'Ελληνικά' },
  { code: 'uk', name: 'Ukrainian', localName: 'Українська' },
  { code: 'ru', name: 'Russian', localName: 'Русский' },
  { code: 'tr', name: 'Turkish', localName: 'Türkçe' },
  { code: 'sv', name: 'Swedish', localName: 'Svenska' },
  { code: 'da', name: 'Danish', localName: 'Dansk' },
  { code: 'no', name: 'Norwegian', localName: 'Norsk' },
  { code: 'fi', name: 'Finnish', localName: 'Suomi' },
  { code: 'ja', name: 'Japanese', localName: '日本語' },
  { code: 'ko', name: 'Korean', localName: '한국어' },
  { code: 'zh', name: 'Chinese', localName: '中文' },
  { code: 'ar', name: 'Arabic', localName: 'العربية' },
  { code: 'he', name: 'Hebrew', localName: 'עברית' },
  { code: 'fa', name: 'Persian', localName: 'فارسی' },
  { code: 'hi', name: 'Hindi', localName: 'हिन्दी' },
  { code: 'bn', name: 'Bengali', localName: 'বাংলা' },
  { code: 'th', name: 'Thai', localName: 'ไทย' },
  { code: 'vi', name: 'Vietnamese', localName: 'Tiếng Việt' },
  { code: 'id', name: 'Indonesian', localName: 'Bahasa Indonesia' },
  { code: 'ms', name: 'Malay', localName: 'Bahasa Melayu' },
  { code: 'sw', name: 'Swahili', localName: 'Kiswahili' },
  { code: 'ta', name: 'Tamil', localName: 'தமிழ்' },
];

interface TranslateLanguageSelectorProps {
  value: string;
  onChange: (language: string) => void;
}

export function TranslateLanguageSelector({ value, onChange }: TranslateLanguageSelectorProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [customLang, setCustomLang] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!search) return LANGUAGES;
    const q = search.toLowerCase();
    return LANGUAGES.filter(
      (l) => l.code.toLowerCase() === q || l.name.toLowerCase().includes(q) || l.localName.toLowerCase().includes(q),
    );
  }, [search]);

  const selectedLang = LANGUAGES.find((l) => l.localName === value || l.name === value);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (lang: Language) => {
    onChange(lang.localName);
    setCustomLang('');
    setSearch('');
    setIsOpen(false);
  };

  const handleCustomChange = (v: string) => {
    setCustomLang(v);
    if (v.trim()) onChange(v.trim());
  };

  const handleClear = () => {
    onChange('');
    setCustomLang('');
    setSearch('');
  };

  return (
    <div id="text-tool-translate-language" className="space-y-3">
      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
        {t('textTools.translate.selectLanguage')}
      </label>

      {/* Dropdown selector */}
      <div ref={wrapperRef} className="relative">
        <div
          className="flex items-center gap-2 w-full px-3 py-2.5 border border-neutral-300 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
        >
          <Search className="w-4 h-4 text-neutral-400 flex-shrink-0" />
          {isOpen ? (
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('textTools.translate.searchLanguage')}
              className="flex-1 bg-transparent outline-none text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span className={`flex-1 text-sm ${selectedLang ? 'text-neutral-900 dark:text-neutral-100' : 'text-neutral-400'}`}>
              {selectedLang ? `${selectedLang.localName} (${selectedLang.code.toUpperCase()})` : t('textTools.translate.searchLanguage')}
            </span>
          )}
          {value && !customLang && (
            <button onClick={(e) => { e.stopPropagation(); handleClear(); }} className="p-0.5 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded">
              <X className="w-3.5 h-3.5 text-neutral-400" />
            </button>
          )}
        </div>

        {isOpen && (
          <ul className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 shadow-lg">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-neutral-400">{t('textTools.translate.noResults')}</li>
            ) : (
              filtered.map((lang) => (
                <li
                  key={lang.code}
                  onClick={() => handleSelect(lang)}
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-primary-50 dark:hover:bg-primary-900/20 ${
                    value === lang.localName ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-medium' : 'text-neutral-700 dark:text-neutral-300'
                  }`}
                >
                  {lang.localName} <span className="text-neutral-400">({lang.code.toUpperCase()}) — {lang.name}</span>
                </li>
              ))
            )}
          </ul>
        )}
      </div>

      {/* Free-text fallback */}
      <div>
        <label className="block text-xs text-neutral-500 dark:text-neutral-400 mb-1">
          {t('textTools.translate.orCustom')}
        </label>
        <input
          type="text"
          value={customLang}
          onChange={(e) => handleCustomChange(e.target.value)}
          placeholder={t('textTools.translate.customLanguagePlaceholder')}
          className="w-full px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400"
        />
      </div>
    </div>
  );
}
